import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSettingsStore } from '../stores/appSettingsStore';

/**
 * Lightens a hex color for use as a background color
 * This function ensures good contrast for text by creating a light background
 * @param hex - The hex color to lighten (e.g., "#2563EB")
 * @param percent - The percentage to lighten (0-100, default 90)
 * @returns The lightened hex color suitable for backgrounds
 */
export const lightenColor = (hex: string, percent: number = 90): string => {
    // Remove # if present
    const cleanHex = hex.replace("#", "");
    
    // Parse RGB values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    
    // Lighten each component
    const newR = Math.min(255, r + Math.round((255 - r) * (percent / 100)));
    const newG = Math.min(255, g + Math.round((255 - g) * (percent / 100)));
    const newB = Math.min(255, b + Math.round((255 - b) * (percent / 100)));
    
    // Convert back to hex
    const toHex = (n: number) => {
        const hex = n.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };
    
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

/**
 * Darkens a hex color by a given percentage
 * @param hex - The hex color to darken (e.g., "#FF5733")
 * @param percent - The percentage to darken (0-100, default 40)
 * @returns The darkened hex color
 */
export const darkenColor = (hex: string, percent: number = 40): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.round((num >> 16) * (1 - percent / 100));
    const g = Math.round(((num >> 8) & 0x00FF) * (1 - percent / 100));
    const b = Math.round((num & 0x0000FF) * (1 - percent / 100));
    return "#" + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
};

/**
 * Gets the background and text colors for a category badge
 * @param categoryColor - The base hex color for the category
 * @param defaultColor - The fallback color if categoryColor is not provided
 * @returns Object with backgroundColor and textColor
 */
export const getCategoryColors = (categoryColor?: string, defaultColor: string = "#2563EB", isDark: boolean = false) => {
    const baseColor = categoryColor || defaultColor;
    return {
        backgroundColor: isDark ? `${baseColor}26` : lightenColor(baseColor), // ~15% alpha over the text color in dark mode
        textColor: baseColor, // Use the original bright color for text
    };
};

/**
 * Component for rendering a category badge with consistent styling
 * @param categoryName - The name of the category to display
 * @param categoryColor - The hex color for the category
 * @param isDefaultCategory - Whether this is the default category
 * @param className - Optional additional CSS classes
 */
interface CategoryBadgeProps {
    categoryName: string;
    categoryColor?: string;
    isDefaultCategory?: boolean;
    className?: string;
}

export const CategoryBadge: React.FC<CategoryBadgeProps> = ({
    categoryName,
    categoryColor,
    isDefaultCategory = false,
    className = "",
}) => {
    const { t } = useTranslation();
    const darkMode = useAppSettingsStore((s) => s.darkMode);
    // Uncategorized items show a muted "Unassigned" so it's obvious a category
    // can still be assigned (clicking the badge opens the assignment flow).
    if (categoryName === "Uncategorized") {
        return (
            <span className={`text-[#6B7280] dark:text-textTertiary-dark ${className}`}>
                {t('common.unassigned')}
            </span>
        );
    }

    // Use blue color for default category
    const defaultBlueColor = "#2563EB";
    const effectiveColor = isDefaultCategory ? defaultBlueColor : categoryColor;
    const { backgroundColor, textColor } = getCategoryColors(effectiveColor, defaultBlueColor, darkMode);

    return (
        <span
            className={`inline-flex items-center rounded-md p-2 text-[14px] leading-[10px] font-medium ${className}`}
            style={{
                backgroundColor,
                color: textColor,
            }}
        >
            {categoryName}
        </span>
    );
};
